import type { LetterHead } from "@repo/types";
import type { ColumnDef } from "@tanstack/react-table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@repo/ui/components/Base/DropDown";
import { Button } from "@repo/ui/components/Base/Button";
import { MoreHorizontal, SquareArrowOutUpRight } from "lucide-react";
import { DataTableColumnHeader } from "@repo/ui/components/Base/DataTableColumnHeader";
import {
  createCreatedAtColumn,
  createIdColumn,
  createStatusColumn,
  createUpdatedAtColumn,
} from "../dataTable/columns";
import type { TableLayoutState } from "@repo/ui/components/Base/DataTable";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";
import { useMemo } from "react";
import { useUserStore } from "@/stores/userStore";
import { cn } from "@repo/shared/utils";

const createViewNameColumn = (): ColumnDef<LetterHead> => ({
  accessorKey: "viewName",
  meta: {
    label: "Name",
  },
  sortingFn: "text",
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Name" />
  ),
});

const createOrgNameColumn = (): ColumnDef<LetterHead> => ({
  accessorKey: "name",
  meta: {
    label: "Organization Name",
  },
  sortingFn: "text",
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Organization Name" />
  ),
});

export const useColumns = ({
  handleEdit,
  handleView: _handleView,
  handleArchive,
}: {
  handleEdit: (id: string) => void;
  handleView?: (id: string) => void;
  handleArchive: (id: string) => void;
}) => {
  const hadHandleView = !!_handleView;

  handleEdit = useFutureCallback(handleEdit);
  const handleView = useFutureCallback(_handleView ?? (() => {}));
  handleArchive = useFutureCallback(handleArchive);

  const col: ColumnDef<LetterHead>[] = useMemo(
    () => [
      {
        id: "actions",
        header: "",
        cell: function Cell({ row }) {
          const team = useUserStore((s) => s.activeTeam);
          const isFullOrg = !team || team === "org";

          const letterhead = row.original;
          return (
            <div className="flex flex-row align-middle gap-1 justify-start">
              <Button
                variant="ghost"
                tooltip={
                  isFullOrg ? "Pick a team first" : "Open this letterhead"
                }
                className={cn(
                  "h-8 w-8 p-0",
                  isFullOrg && "opacity-50 cursor-not-allowed"
                )}
                disabled={isFullOrg}
                onClick={() => handleEdit(letterhead.id)}
              >
                <SquareArrowOutUpRight />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="h-8 w-8 p-0">
                    <span className="sr-only">Open menu</span>
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Actions</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {hadHandleView && (
                    <DropdownMenuItem onClick={() => handleView(letterhead.id)}>
                      View
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    disabled={isFullOrg}
                    tooltip={isFullOrg ? "Pick a team first" : ""}
                    onClick={() => handleEdit(letterhead.id)}
                  >
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleArchive(letterhead.id)}
                  >
                    Archive
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigator.clipboard.writeText(letterhead.id)}
                  >
                    Copy letter head ID
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
        enableHiding: false,
      },
      createIdColumn(),
      createViewNameColumn(),
      createOrgNameColumn(),
      createStatusColumn(),
      createCreatedAtColumn(),
      createUpdatedAtColumn(),
    ],
    [hadHandleView, handleEdit, handleView, handleArchive]
  );
  return col;
};

export const layoutDefaults: TableLayoutState = {
  columnVisibility: {
    id: false,
    updatedAt: false,
  },
};
